<template>
  <div :id="field.name + 'a'"
       class="m-signature-pad"
       :class="'signature-pad-'+field.name"
       @click.capture="onMouseOver"
       @mouseover.capture="onMouseOver"
  >
    <div class="m-signature-pad--body"
         ref="signatureRef">
      <img v-if="locked"
           :src="value"
           class="signature-image"
           alt="">
      <canvas v-if="!locked"
              :id="field.name+'-canvas'"
              ref="signatureCanvasRef"/>
      <input type="hidden"
             :value="signatureData"
             v-validate="{required: required && !field.hide}"
             :data-vv-name="field.name"
             :data-vv-as="field.description.replace(/\./g, '')"
      />
    </div>
    <div class="m-signature-pad--footer">
      <div class="description"></div>
      <button type="button"
              class="button clear"
              @click="clearSignature">
        Clear
      </button>
      <span v-if="showClearMessage"
            class="clear-message">
        Please clear previous signature before creating new one.
      </span>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";

import SignaturePad from 'signature_pad';

export default {
  props: {
    value: {
      type: String,
      required: false
    },
    field: {
      type: Object,
      required: true
    },
    required: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    }
  },

  emits: ['input'],

  setup(props, { emit }) {
    const signatureRef = ref();
    const signatureCanvasRef = ref();
    const signaturePad = ref(null);
    const signatureData = ref('');

    const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);

    // show message about clearing if there was signature before
    // TODO: check if it's working
    const showClearMessage = ref(!!props.value);

    const onResize = () => {
      signatureCanvasRef.value.width = signatureRef.value.offsetWidth * pixelRatio;
      signatureCanvasRef.value.height = signatureRef.value.offsetHeight * pixelRatio;
      signatureCanvasRef.value.getContext("2d").scale(pixelRatio, pixelRatio);
      signaturePad.value.clear();
      nextTick(() => signaturePad.value && signaturePad.value.fromDataURL(signatureData.value))
    }

    onMounted(() => {
      window.addEventListener("resize", onResize);
      // TODO: check if it's working when locked
      if (!props.locked){
        signaturePad.value = new SignaturePad(signatureCanvasRef.value, {
          onEnd: () => {
            signatureData.value = signaturePad.value.toDataURL()
            emit('input', signaturePad.value.toDataURL());
            nextTick(() => emit('validate', ''));
          }
        })
        if (props.value) {
          onResize();
          nextTick(() => signaturePad.value.fromDataURL(props.value))
        }
        setTimeout(() => onResize(), 100);
      }
    })

    onUnmounted(() => {
      window.removeEventListener("resize", onResize);
    });

    const clearSignature = () => {
      showClearMessage.value = false;
      signatureData.value = '';
      emit('input', '');
      nextTick(() => emit('validate', ''));
      signaturePad.value.clear();
    }

    watch(() => props.value, (newVal, oldVal) => {
      if (typeof oldVal === 'undefined' && newVal) {
        onResize();
        signatureData.value = newVal;
        nextTick(() => signaturePad.value.fromDataURL(newVal));
      }
    })

    const onMouseOver = () => {
      if (signatureCanvasRef.value.width === 0) {
        onResize();
      }
    }

    return {
      signatureRef,
      signatureCanvasRef,
      showClearMessage,
      clearSignature,
      signatureData,
      // XXX: not used in the template, but triggered by accordion
      onMouseOver,
      onResize,
    }
  }
}
</script>

<style scoped lang="scss">

.m-signature-pad {
  position: relative;
  font-size: 10px;
  width: 100%;
  height: 250px;
  border: 1px solid #e8e8e8;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.27), 0 0 40px rgba(0, 0, 0, 0.08) inset;
  border-radius: 4px;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.m-signature-pad:before, .m-signature-pad:after {
  position: absolute;
  z-index: -1;
  content: "";
  width: 40%;
  height: 10px;
  left: 20px;
  bottom: 10px;
  background: transparent;
  -webkit-transform: skew(-3deg) rotate(-3deg);
  -moz-transform: skew(-3deg) rotate(-3deg);
  -ms-transform: skew(-3deg) rotate(-3deg);
  -o-transform: skew(-3deg) rotate(-3deg);
  transform: skew(-3deg) rotate(-3deg);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.4);
}

.m-signature-pad:after {
  left: auto;
  right: 20px;
  -webkit-transform: skew(3deg) rotate(3deg);
  -moz-transform: skew(3deg) rotate(3deg);
  -ms-transform: skew(3deg) rotate(3deg);
  -o-transform: skew(3deg) rotate(3deg);
  transform: skew(3deg) rotate(3deg);
}

.m-signature-pad--body {
  position: absolute;
  left: 20px;
  right: 20px;
  top: 20px;
  bottom: 60px;
  border: 1px solid #f4f4f4;
}

.m-signature-pad .signature-image {
  display: none;
  width: 100%;
  height: 100%;
}

.m-signature-pad--body canvas {
  position: absolute;
  height: 168px;
  width: 100%;
  left: 0;
  top: 0;
  border-radius: 4px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.02) inset;
}

.m-signature-pad--footer {
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 20px;
  height: 40px;
}

.m-signature-pad--footer .description {
  color: #C3C3C3;
  text-align: center;
  font-size: 1.2em;
  margin-top: 1.8em;
}

.m-signature-pad--footer .button {
  position: absolute;
  bottom: 0;
}

.m-signature-pad--footer .button.clear {
  left: 0;
}


.m-signature-pad--footer .button.save {
  right: 0;
  display: none;
}

.m-signature-pad--footer .clear-message {
  display: none;
  color: #f00;
  margin-left: 60px;
}

@media screen and (max-width: 1024px) {
  .m-signature-pad {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    height: auto;
    min-width: 250px;
    min-height: 240px;
    margin: 5%;
  }
}

@media screen and (min-device-width: 768px) and (max-device-width: 1024px) {
  .m-signature-pad {
    margin: 10%;
  }
}

@media screen and (max-height: 320px) {
  .m-signature-pad--body {
    left: 0;
    right: 0;
    top: 0;
    bottom: 32px;
  }

  .m-signature-pad--footer {
    left: 20px;
    right: 20px;
    bottom: 4px;
    height: 28px;
  }

  .m-signature-pad--footer .description {
    font-size: 1em;
    margin-top: 1em;
  }
}

</style>



// WEBPACK FOOTER //
// SignatureRender.vue?2842362c